// App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ScrollToTop from './components/layout/ScrollToTop';
import NotificationPermissionPrompt from './components/notifications/NotificationPermissionPrompt';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import Home from './pages/home/Home';
import ListingDetail from './pages/listing/ListingDetail';
import Search from './pages/Search';
import HowToBuy from './pages/HowToBuy';
import HowToSell from './pages/HowToSell';
import Faq from './pages/Faq';
import Escrow from './pages/Escrow';
import ContactUs from './pages/ContactUs';
import AboutUs from './pages/AboutUs';
import LegalPages from './pages/LegalPages';
import RecommendationsPage from './pages/home/RecommendationsPage';
import Profile from './pages/profile/Profile';
import MyOrders from './pages/profile/MyOrders';
import Favorites from './pages/profile/Favorites';
import Notifications from './pages/profile/Notifications';
import OrderDetail from './pages/profile/OrderDetail';
import CategoryPage from './pages/category/CategoryPage';
import CategoriesListingPage from './pages/category/CategoriesListingPage';
import ChatsScreen from './pages/chats/ChatsScreen';
import ChatDetail from './pages/chats/ChatDetail';
import SellerDetail from './pages/listing/SellerDetail';
import MyListings from './pages/profile/MyListings';
import Performance from './pages/profile/Performance';
import Settings from './pages/profile/Settings';
import Wallet from './pages/wallet/index';
import Deposit from './pages/wallet/Deposit';
import Withdraw from './pages/wallet/Withdraw';
import WithdrawOTP from './pages/wallet/WithdrawOTP';
import Transactions from './pages/wallet/Transactions';
import TransactionDetail from './pages/wallet/TransactionDetail';
import PayoutMethod from './pages/wallet/PayoutMethod';
import Support from './pages/support/Support';
import NewTicket from './pages/support/New';
import TicketDetail from './pages/support/TicketDetail';
import StoreDashboard from './pages/store/Dashboard';
import StoreImages from './pages/store/Store-Images';
import SetupStore from './pages/store/Setup-Store';
import StoreSettings from './pages/store/store-settings';
import StoreListings from './pages/store/listings/listings';
import CreateListing from './pages/store/listings/create-listing';
import StoreListingDetails from './pages/store/listings/ListingDetails';
import StoreEditListing from './pages/store/listings/edit-listing';
import ListingPerformance from './pages/store/listings/Performance';
import BoostPackages from './pages/store/boost-package/index';
import BoostListing from './pages/store/boost-package/boost-listing';
import BoostPayment from './pages/store/boost-package/BoostPaymentPage';
import BoostHistory from './pages/store/boost-package/boost-history';
import BoostGuide from './pages/store/boost-package/BoostGuide';
import EscrowPaymentPage from './pages/chats/EscrowPaymentPage';
import RaiseDispute from './pages/dispute/RaiseDispute';
import DisputeList from './pages/dispute/DisputeList';
import DisputeDetail from './pages/dispute/DisputeDetail';
import ProtectedRoute from './components/auth/ProtectedRoute';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <ScrollToTop />
          <NotificationPermissionPrompt />
          
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/how-to-buy" element={<HowToBuy />} />
            <Route path="/how-to-sell" element={<HowToSell />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/escrow" element={<Escrow />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/legal" element={<LegalPages />} />
            <Route path="/recommendations" element={<RecommendationsPage />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/seller/:id" element={<SellerDetail />} />
            <Route path="/categories" element={<CategoriesListingPage />} />
            <Route path="/category" element={<CategoryPage />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            
            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/my-orders/:id" element={<OrderDetail />} />
              <Route path="/my-listings" element={<MyListings />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/chats" element={<ChatsScreen />} />
              <Route path="/chats/:id" element={<ChatDetail />} />
              <Route path="/chats/:id/escrow-payment" element={<EscrowPaymentPage />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/wallet/deposit" element={<Deposit />} />
              <Route path="/wallet/withdraw" element={<Withdraw />} />
              <Route path="/wallet/withdraw-otp" element={<WithdrawOTP />} />
              <Route path="/wallet/transactions" element={<Transactions />} />
              <Route path="/wallet/transactions/:id" element={<TransactionDetail />} />
              <Route path="/wallet/payout-methods" element={<PayoutMethod />} />
              <Route path="/disputes" element={<DisputeList />} />
              <Route path="/disputes/:id" element={<DisputeDetail />} />
              <Route path="/disputes/raise" element={<RaiseDispute />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/new-ticket" element={<NewTicket />} />
              <Route path="/support/tickets/:id" element={<TicketDetail />} />
              <Route path="/store" element={<StoreDashboard />} />
              <Route path="/store/store-images" element={<StoreImages />} />
              <Route path="/store/setup-store" element={<SetupStore />} />
              <Route path="/store/settings" element={<StoreSettings />} />
              <Route path="/store/listings" element={<StoreListings />} />
              <Route path="/store/listings/create-listing" element={<CreateListing />} />
              <Route path="/store/listings/listing-details/:id" element={<StoreListingDetails />} />
              <Route path="/store/listings/edit-listing/:id" element={<StoreEditListing />} />
              <Route path="/store/listings/performance/:id" element={<ListingPerformance />} />
              <Route path="/store/boost-package/listings" element={<BoostPackages />} />
              <Route path="/store/boost-package/:id" element={<BoostListing />} />
              <Route path="/store/boost/payment/:listingId" element={<BoostPayment />} />
              <Route path="/store/boost-history" element={<BoostHistory />} />
              <Route path="/store/boost-package/boost-guide" element={<BoostGuide />} />
            </Route>
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;